import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Form,
  Modal,
  Badge,
  Spinner,
} from 'react-bootstrap';
import { FaFileInvoice, FaPlus, FaEdit, FaFilter } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import ingresosCorporativoApi from '../../services/api/ingresosCorporativoApi';
import depositosApi from '../../services/api/depositosApi';
import 'react-toastify/dist/ReactToastify.css';

const hoy = () => new Date().toISOString().split('T')[0];

const emptyForm = {
  cliente_facturacion_id: '',
  centro_costo_id: '',
  serie: '',
  numero: '',
  fecha_emision: hoy(),
  fecha_vencimiento: '',
  moneda: 'PEN',
  subtotal: '',
  monto_igv: '',
  monto_total: '',
  estado: 'registrada',
  observaciones: '',
};

const FacturasVenta = () => {
  const [items, setItems] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [centros, setCentros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [filtros, setFiltros] = useState({
    centro_costo_id: '',
    cliente_facturacion_id: '',
    fecha_desde: '',
    fecha_hasta: '',
  });
  const [mostrarFiltros, setMostrarFiltros] = useState(true);

  const loadCatalogos = useCallback(async () => {
    try {
      const [cRes, ccRes] = await Promise.all([
        ingresosCorporativoApi.getClientesFacturacion(true),
        depositosApi.getCentrosCosto(true),
      ]);
      setClientes(Array.isArray(cRes.data) ? cRes.data : []);
      setCentros(Array.isArray(ccRes.data) ? ccRes.data : []);
    } catch (e) {
      console.error(e);
      toast.error('Error al cargar catálogos');
    }
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = { solo_activas: false };
      if (filtros.centro_costo_id) params.centro_costo_id = parseInt(filtros.centro_costo_id, 10);
      if (filtros.cliente_facturacion_id)
        params.cliente_facturacion_id = parseInt(filtros.cliente_facturacion_id, 10);
      if (filtros.fecha_desde) params.fecha_desde = filtros.fecha_desde;
      if (filtros.fecha_hasta) params.fecha_hasta = filtros.fecha_hasta;
      const response = await ingresosCorporativoApi.getFacturasVenta(params);
      setItems(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Error al cargar facturas');
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => {
    loadCatalogos();
  }, [loadCatalogos]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros((prev) => ({ ...prev, [name]: value }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const recalcTotal = () => {
    const s = parseFloat(formData.subtotal) || 0;
    const i = parseFloat(formData.monto_igv) || 0;
    setFormData((prev) => ({ ...prev, monto_total: (s + i).toFixed(2) }));
  };

  const openNew = () => {
    setEditing(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setFormData({
      cliente_facturacion_id: String(row.cliente_facturacion_id),
      centro_costo_id: String(row.centro_costo_id),
      serie: row.serie || '',
      numero: row.numero || '',
      fecha_emision: String(row.fecha_emision || '').slice(0, 10),
      fecha_vencimiento: row.fecha_vencimiento ? String(row.fecha_vencimiento).slice(0, 10) : '',
      moneda: row.moneda || 'PEN',
      subtotal: row.subtotal != null ? String(row.subtotal) : '',
      monto_igv: row.monto_igv != null ? String(row.monto_igv) : '',
      monto_total: row.monto_total != null ? String(row.monto_total) : '',
      estado: row.estado || 'registrada',
      observaciones: row.observaciones || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.cliente_facturacion_id || !formData.centro_costo_id) {
      toast.error('Cliente y centro de costo son obligatorios');
      return;
    }
    if (!formData.numero?.trim()) {
      toast.error('El número de factura es obligatorio');
      return;
    }
    const sub = parseFloat(formData.subtotal) || 0;
    const igv = parseFloat(formData.monto_igv) || 0;
    let total = parseFloat(formData.monto_total);
    if (!Number.isFinite(total) || total <= 0) total = sub + igv;
    setLoading(true);
    try {
      const payload = {
        cliente_facturacion_id: parseInt(formData.cliente_facturacion_id, 10),
        centro_costo_id: parseInt(formData.centro_costo_id, 10),
        serie: formData.serie?.trim() || '',
        numero: formData.numero.trim(),
        fecha_emision: formData.fecha_emision,
        fecha_vencimiento: formData.fecha_vencimiento?.trim() || null,
        moneda: formData.moneda,
        subtotal: sub,
        monto_igv: igv,
        monto_total: total,
        estado: formData.estado,
        observaciones: formData.observaciones?.trim() || null,
        activo: true,
      };
      if (editing) {
        await ingresosCorporativoApi.updateFacturaVenta(editing.id, payload);
        toast.success('Factura actualizada');
      } else {
        await ingresosCorporativoApi.createFacturaVenta(payload);
        toast.success('Factura registrada');
      }
      setShowModal(false);
      loadItems();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`¿Anular registro de factura ${row.serie || ''}-${row.numero}?`)) return;
    setLoading(true);
    try {
      await ingresosCorporativoApi.deleteFacturaVenta(row.id);
      toast.success('Factura anulada');
      loadItems();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setLoading(false);
    }
  };

  const fmtMonto = (row) => {
    const sym = row.moneda === 'USD' ? '$' : row.moneda === 'EUR' ? '€' : 'S/';
    const n = parseFloat(row.monto_total);
    return `${sym} ${Number.isFinite(n) ? n.toFixed(2) : '0.00'}`;
  };

  return (
    <Container fluid className="py-3">
      <ToastContainer />
      <Row className="mb-3 align-items-center">
        <Col>
          <h4 className="mb-0">
            <FaFileInvoice className="me-2 text-success" />
            Facturas de venta (ingresos)
          </h4>
          <small className="text-muted">
            Registro de ingresos por cliente y centro de costo (mismo criterio que depósitos/gastos).
          </small>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={() => setMostrarFiltros(!mostrarFiltros)}>
            <FaFilter className="me-1" /> Filtros
          </Button>
          <Button variant="success" onClick={openNew}>
            <FaPlus className="me-1" /> Nueva factura
          </Button>
        </Col>
      </Row>

      {mostrarFiltros && (
        <Card className="mb-3">
          <Card.Body>
            <Row className="g-2">
              <Col md={3}>
                <Form.Label className="small mb-1">Centro de costo</Form.Label>
                <Form.Select name="centro_costo_id" value={filtros.centro_costo_id} onChange={handleFiltroChange}>
                  <option value="">Todos</option>
                  {centros.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.codigo} — {c.nombre}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label className="small mb-1">Cliente</Form.Label>
                <Form.Select
                  name="cliente_facturacion_id"
                  value={filtros.cliente_facturacion_id}
                  onChange={handleFiltroChange}
                >
                  <option value="">Todos</option>
                  {clientes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.razon_social}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label className="small mb-1">Fecha desde</Form.Label>
                <Form.Control type="date" name="fecha_desde" value={filtros.fecha_desde} onChange={handleFiltroChange} />
              </Col>
              <Col md={3}>
                <Form.Label className="small mb-1">Fecha hasta</Form.Label>
                <Form.Control type="date" name="fecha_hasta" value={filtros.fecha_hasta} onChange={handleFiltroChange} />
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      <Card>
        <Card.Body className="p-0">
          {loading && !items.length ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle small">
                <thead className="table-light">
                  <tr>
                    <th>Fecha</th>
                    <th>Documento</th>
                    <th>Cliente</th>
                    <th>Centro de costo</th>
                    <th>Total</th>
                    <th>Estado</th>
                    <th style={{ width: 100 }} />
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.id}>
                      <td className="text-nowrap">{String(row.fecha_emision || '').slice(0, 10)}</td>
                      <td>
                        {(row.serie ? `${row.serie}-` : '') + row.numero}
                      </td>
                      <td>{row.cliente_razon_social || '—'}</td>
                      <td>
                        <span className="text-muted">{row.centro_costo_codigo}</span>{' '}
                        {row.centro_costo_nombre}
                      </td>
                      <td className="text-end fw-medium">{fmtMonto(row)}</td>
                      <td>
                        <Badge bg={row.activo ? 'primary' : 'secondary'}>{row.estado}</Badge>
                      </td>
                      <td className="text-nowrap">
                        <Button variant="outline-primary" size="sm" className="me-1" onClick={() => openEdit(row)}>
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(row)}
                          disabled={!row.activo}
                        >
                          Anular
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="text-center text-muted py-4">No hay facturas con los filtros actuales.</div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editing ? 'Editar factura' : 'Nueva factura de venta'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row className="g-2">
              <Col md={6}>
                <Form.Label>Cliente *</Form.Label>
                <Form.Select
                  name="cliente_facturacion_id"
                  value={formData.cliente_facturacion_id}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Seleccione…</option>
                  {clientes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.razon_social}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label>Centro de costo *</Form.Label>
                <Form.Select
                  name="centro_costo_id"
                  value={formData.centro_costo_id}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Seleccione…</option>
                  {centros.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.codigo} — {c.nombre}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label>Serie</Form.Label>
                <Form.Control name="serie" value={formData.serie} onChange={handleInputChange} placeholder="F001" />
              </Col>
              <Col md={3}>
                <Form.Label>Número *</Form.Label>
                <Form.Control name="numero" value={formData.numero} onChange={handleInputChange} required />
              </Col>
              <Col md={3}>
                <Form.Label>Emisión *</Form.Label>
                <Form.Control type="date" name="fecha_emision" value={formData.fecha_emision} onChange={handleInputChange} required />
              </Col>
              <Col md={3}>
                <Form.Label>Vencimiento</Form.Label>
                <Form.Control
                  type="date"
                  name="fecha_vencimiento"
                  value={formData.fecha_vencimiento}
                  onChange={handleInputChange}
                />
              </Col>
              <Col md={3}>
                <Form.Label>Moneda</Form.Label>
                <Form.Select name="moneda" value={formData.moneda} onChange={handleInputChange}>
                  <option value="PEN">PEN</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Label>Subtotal</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="subtotal"
                  value={formData.subtotal}
                  onChange={handleInputChange}
                  onBlur={recalcTotal}
                />
              </Col>
              <Col md={3}>
                <Form.Label>IGV</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="monto_igv"
                  value={formData.monto_igv}
                  onChange={handleInputChange}
                  onBlur={recalcTotal}
                />
              </Col>
              <Col md={3}>
                <Form.Label>Total</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="monto_total"
                  value={formData.monto_total}
                  onChange={handleInputChange}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Estado</Form.Label>
                <Form.Select name="estado" value={formData.estado} onChange={handleInputChange}>
                  <option value="registrada">Registrada</option>
                  <option value="pagada">Pagada</option>
                  <option value="anulada">Anulada</option>
                </Form.Select>
              </Col>
              <Col md={12}>
                <Form.Label>Observaciones</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="observaciones"
                  value={formData.observaciones}
                  onChange={handleInputChange}
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" type="button" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button variant="success" type="submit" disabled={loading}>
              {loading ? <Spinner size="sm" /> : 'Guardar'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default FacturasVenta;
